import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useSingleFlight } from '../hooks/useSingleFlight';
import Cookies from 'js-cookie';
import config, { isDefaultCluster } from '../Config';
import Loader from './Loader';
import { useNavigate } from 'react-router-dom';
import { HandleResponse } from '../functions/HandleResponse';
import { authFetch } from '../functions/authFetch';
import { renewTokenIfStale, getCookieToken } from '../functions/authSession';
import './pages.css';

/** After upload finishes, if the server holds the connection open with no response (long build), unlock UI. */
const RESPONSE_WAIT_ASSUME_BACKEND_MS = 60_000;

/** Upload formData via XHR so we can track upload progress without a timeout. */
function uploadWithProgress(url, formData, token, onProgress) {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    let responseWaitTimer = null;
    let abortedAssumeBackend = false;
    let uploadFinished = false;
    let settled = false;

    const clearResponseWaitTimer = () => {
      if (responseWaitTimer != null) {
        clearTimeout(responseWaitTimer);
        responseWaitTimer = null;
      }
    };

    const resolveAssumeBackend = () => {
      if (settled) return;
      settled = true;
      clearResponseWaitTimer();
      resolve({
        ok: true,
        status: 200,
        assumedBackendNoResponse: true,
        json: () => Promise.resolve({}),
      });
    };

    xhr.open('POST', url);
    xhr.setRequestHeader('Authorization', `Bearer ${token}`);
    xhr.upload.onprogress = (e) => {
      if (e.lengthComputable) onProgress(Math.round((e.loaded / e.total) * 100));
    };
    xhr.upload.onload = () => {
      uploadFinished = true;
      onProgress(100);
      responseWaitTimer = window.setTimeout(() => {
        abortedAssumeBackend = true;
        xhr.abort();
      }, RESPONSE_WAIT_ASSUME_BACKEND_MS);
    };
    xhr.onload = () => {
      if (settled) return;
      clearResponseWaitTimer();
      const status = xhr.status;
      // ALB/nginx often returns 504/502/503 while the build may still run; browsers may also surface that as onerror.
      if (status === 504 || status === 502 || status === 503) {
        resolveAssumeBackend();
        return;
      }
      settled = true;
      resolve({
        ok: status >= 200 && status < 300,
        status,
        json: () => {
          try { return Promise.resolve(JSON.parse(xhr.responseText)); }
          catch { return Promise.reject(new Error('Invalid JSON')); }
        },
      });
    };
    xhr.onerror = () => {
      if (settled) return;
      // After the body is uploaded, a "network" error is often a gateway timeout or dropped long poll — not a failed upload.
      if (abortedAssumeBackend || uploadFinished) {
        resolveAssumeBackend();
        return;
      }
      settled = true;
      clearResponseWaitTimer();
      reject(new Error('Network error'));
    };
    xhr.onabort = () => {
      if (settled) return;
      if (abortedAssumeBackend) {
        resolveAssumeBackend();
      } else {
        settled = true;
        clearResponseWaitTimer();
        reject(new Error('Request aborted'));
      }
    };
    xhr.send(formData);
  });
}

const SORT_NAME = 'name';
const SORT_SIZE = 'size';
const SORT_DATE = 'date';

const MCRManagement = () => {
  const navigate = useNavigate();
  const isDefault = isDefaultCluster();
  const [images, setImages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pushLoading, setPushLoading] = useState(false);
  const [deleting, setDeleting] = useState(null);
  const [sortBy, setSortBy] = useState(SORT_NAME);
  const [sortAsc, setSortAsc] = useState(true);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [imageName, setImageName] = useState('');
  const [zipFile, setZipFile] = useState(null);
  const [imageType, setImageType] = useState('short');
  const [registryView, setRegistryView] = useState('short');
  const [pushMessage, setPushMessage] = useState(null);
  const [pushError, setPushError] = useState(null);
  const [uploadProgress, setUploadProgress] = useState(null);
  const [showDeployWarning, setShowDeployWarning] = useState(false);
  const runPush = useSingleFlight();
  const runDelete = useSingleFlight();

  // ── push source tab ───────────────────────────────────────────────────────────
  const [pushSource, setPushSource] = useState('zip'); // 'zip' | 'github'

  // ── from-github push state ────────────────────────────────────────────────────
  const [ghConnected, setGhConnected] = useState(null); // null=loading, true/false
  const [ghConnecting, setGhConnecting] = useState(false);
  const ghPollRef = useRef(null);
  const ghPollTimeoutRef = useRef(null);
  const [ghRepos, setGhRepos] = useState([]);
  const [ghReposLoading, setGhReposLoading] = useState(false);
  const [ghSelectedRepo, setGhSelectedRepo] = useState('');
  const [ghBranches, setGhBranches] = useState([]);
  const [ghBranchesLoading, setGhBranchesLoading] = useState(false);
  const [ghSelectedBranch, setGhSelectedBranch] = useState('');
  const [ghImageName, setGhImageName] = useState('');
  const [ghPushLoading, setGhPushLoading] = useState(false);
  const [ghPushMessage, setGhPushMessage] = useState(null);
  const [ghPushError, setGhPushError] = useState(null);

  const ghBase = `${config.RUNNER_API}/v1/github`;

  const checkGhStatus = useCallback(async () => {
    try {
      const res = await authFetch(`${ghBase}/status`, { method: 'GET' });
      if (res.ok) {
        const data = await res.json();
        setGhConnected(data.connected === true);
        return data.connected === true;
      }
    } catch { }
    setGhConnected(false);
    return false;
  }, [ghBase]);

  useEffect(() => {
    if (pushSource === 'github' && ghConnected === null) {
      checkGhStatus();
    }
  }, [pushSource, ghConnected, checkGhStatus]);

  // The list comes from the GitHub App installation, so it changes whenever the user
  // edits its repository access — which they can do from this very form. Refetching on
  // demand saves them reloading the page to see a repo they just granted.
  const loadGhRepos = useCallback(async () => {
    setGhReposLoading(true);
    try {
      const res = await authFetch(`${ghBase}/repos`, { method: 'GET' });
      if (res.ok) {
        const data = await res.json();
        const repos = Array.isArray(data) ? data : [];
        setGhRepos(repos);
        // A repo whose access was just revoked can no longer be built from.
        setGhSelectedRepo(prev => (repos.some(r => r.full_name === prev) ? prev : ''));
      }
    } catch { /* keep whatever list is already on screen */ }
    finally { setGhReposLoading(false); }
  }, [ghBase]);

  useEffect(() => {
    if (ghConnected && ghRepos.length === 0) loadGhRepos();
  }, [ghConnected, ghRepos.length, loadGhRepos]);

  useEffect(() => {
    if (!ghSelectedRepo) { setGhBranches([]); setGhSelectedBranch(''); return; }
    setGhBranchesLoading(true);
    authFetch(`${ghBase}/branches?repo=${encodeURIComponent(ghSelectedRepo)}`, { method: 'GET' })
      .then(r => r.ok ? r.json() : [])
      .then(data => { setGhBranches(Array.isArray(data) ? data : []); setGhSelectedBranch(''); })
      .catch(() => {})
      .finally(() => setGhBranchesLoading(false));
  }, [ghSelectedRepo, ghBase]);

  const stopGhPoll = useCallback(() => {
    if (ghPollRef.current) { clearInterval(ghPollRef.current); ghPollRef.current = null; }
    if (ghPollTimeoutRef.current) { clearTimeout(ghPollTimeoutRef.current); ghPollTimeoutRef.current = null; }
  }, []);

  const handleGhConnect = async () => {
    setGhConnecting(true);
    try {
      const res = await authFetch(`${ghBase}/connect`, { method: 'GET' });
      if (res.ok) {
        const data = await res.json();
        window.open(data.url, '_blank', 'noopener,noreferrer');
        stopGhPoll();
        ghPollRef.current = setInterval(async () => {
          const connected = await checkGhStatus();
          if (connected) stopGhPoll();
        }, 3000);
        // Auto-stop after 2 minutes if the user never completes installation
        ghPollTimeoutRef.current = setTimeout(stopGhPoll, 2 * 60 * 1000);
      }
    } catch { }
    finally { setGhConnecting(false); }
  };

  useEffect(() => stopGhPoll, [stopGhPoll]);

  const handleGhPush = async (e) => {
    e.preventDefault();
    setGhPushMessage(null);
    setGhPushError(null);
    if (!ghSelectedRepo) { setGhPushError('Select a repository.'); return; }
    if (!ghSelectedBranch) { setGhPushError('Select a branch.'); return; }
    if (!ghImageName.trim()) { setGhPushError('Enter an image name.'); return; }
    setGhPushLoading(true);
    try {
      const res = await authFetch(`${ghBase}/push-from-repo`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          repo: ghSelectedRepo,
          branch: ghSelectedBranch,
          image_name: ghImageName.trim(),
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (res.ok) {
        setGhPushMessage('Build queued — check Build History in a few minutes.');
        setGhImageName('');
        setGhSelectedRepo('');
        setGhSelectedBranch('');
      } else {
        setGhPushError(data.detail || 'Push failed.');
      }
    } catch (err) {
      setGhPushError(err.message || 'Something went wrong.');
    } finally {
      setGhPushLoading(false);
    }
  };

  const fetchImages = useCallback(async (signal, type = registryView) => {
    const token = Cookies.get('runner_app');
    if (!token) {
      navigate('/login');
      return;
    }
    setLoading(true);
    try {
      const response = await authFetch(
        `${config.RUNNER_API}/v1/cdci/mcr/images?image_type=${type}`,
        { method: 'GET', headers: { Authorization: `Bearer ${token}` }, signal },
      );
      await HandleResponse(response, navigate);
      if (response.ok) {
        const data = await response.json();
        setImages(Array.isArray(data) ? data : []);
      } else {
        setImages([]);
      }
    } catch (err) {
      if (err?.name !== 'AbortError') setImages([]);
    } finally {
      if (signal == null || !signal.aborted) setLoading(false);
    }
  }, [navigate, registryView]);

  useEffect(() => {
    const ac = new AbortController();
    fetchImages(ac.signal);
    return () => ac.abort();
  }, [fetchImages]);

  const handleBuildPush = (e) => {
    e.preventDefault();
    setPushMessage(null);
    setPushError(null);
    if (!imageName.trim()) {
      setPushError('Please enter an image name.');
      return;
    }
    if (!zipFile) {
      setPushError('Please select a zip file.');
      return;
    }
    const token = Cookies.get('runner_app');
    if (!token) {
      navigate('/login');
      return;
    }

    // Warn default-cluster users before pushing a deployment image
    if (isDefault && imageType === 'long') {
      setShowDeployWarning(true);
      return;
    }

    doPush();
  };

  const doPush = () => {
    const token = Cookies.get('runner_app');
    if (!token) { navigate('/login'); return; }

    runPush(async () => {
      setPushLoading(true);
      setUploadProgress(0);
      try {
        const formData = new FormData();
        formData.append('zip_file', zipFile);
        formData.append('image_name', imageName.trim());
        formData.append('image_type', imageType);

        // Renew token if needed, then get fresh token for XHR header
        await renewTokenIfStale();
        const freshToken = getCookieToken();
        if (!freshToken) { navigate('/login'); return; }

        const response = await uploadWithProgress(
          `${config.RUNNER_API}/v1/cdci/push-build`,
          formData,
          freshToken,
          (pct) => setUploadProgress(pct),
        );

        await HandleResponse(response, navigate);
        const data = await response.json();
        if (response.ok) {
          if (response.assumedBackendNoResponse) {
            setPushMessage(
              'No response after one minute — the build is likely still running on the server. Check the build log in Build History in a few minutes. You can use the rest of this page meanwhile.'
            );
          } else if (data.buildId) {
            // In-cluster builds stream their log, so there is something to watch now.
            setPushMessage('Build started — follow it live in Build History.');
          } else {
            setPushMessage('Build and Push started — check status in a few minutes.');
          }
          setImageName('');
          setZipFile(null);
          if (document.getElementById('mcr-zip-input-inline')) {
            document.getElementById('mcr-zip-input-inline').value = '';
          }
        } else {
          const errText = data.detail || (typeof data.detail === 'object' ? JSON.stringify(data.detail) : 'Push failed.');
          setPushError(errText);
        }
      } catch (err) {
        setPushError(err.message || 'Something went wrong.');
      } finally {
        setPushLoading(false);
        setUploadProgress(null);
      }
    });
  };

  const handleSort = (column) => {
    if (sortBy === column) {
      setSortAsc((a) => !a);
    } else {
      setSortBy(column);
      setSortAsc(true);
    }
  };

  const handleDeleteClick = (imageName) => {
    setDeleteConfirm(imageName);
  };

  const handleDeleteConfirm = () => {
    const imageName = deleteConfirm;
    setDeleteConfirm(null);
    if (!imageName) return;
    const token = Cookies.get('runner_app');
    if (!token) return;

    runDelete(async () => {
      setDeleting(imageName);
      try {
        const formData = new FormData();
        formData.append('image_name', imageName);
        formData.append('image_type', registryView);
        const response = await authFetch(`${config.RUNNER_API}/v1/cdci/remove-mcr-image`, {
          method: 'POST',
          headers: { Authorization: `Bearer ${token}` },
          body: formData,
        });
        await HandleResponse(response, navigate);
        if (response.ok) {
          setImages((prev) => prev.filter((img) => img.image_name !== imageName));
        }
      } catch (err) {
        console.error(err);
      } finally {
        setDeleting(null);
      }
    });
  };

  const formatDate = (createdOn) => {
    if (!createdOn) return '–';
    try {
      const d = new Date(createdOn);
      return isNaN(d.getTime()) ? createdOn : d.toLocaleString();
    } catch {
      return createdOn;
    }
  };

  const sorted = [...images].sort((a, b) => {
    let cmp = 0;
    if (sortBy === SORT_NAME) {
      cmp = (a.image_name || '').localeCompare(b.image_name || '');
    } else if (sortBy === SORT_SIZE) {
      const sa = Number(a.size_mb) || 0;
      const sb = Number(b.size_mb) || 0;
      cmp = sa - sb;
    } else {
      const da = new Date(a.created_on || 0).getTime();
      const db = new Date(b.created_on || 0).getTime();
      cmp = da - db;
    }
    return sortAsc ? cmp : -cmp;
  });

  return (
    <div className="mcr-page mcr-management">
      <Loader show={loading} />
      <div className="mcr-management-layout">
        <div className="mcr-build-panel">
          <div className="mcr-push-tabs">
            <button
              type="button"
              className={`mcr-push-tab${pushSource === 'zip' ? ' active' : ''}`}
              onClick={() => setPushSource('zip')}
            >
              Upload Zip
            </button>
            <button
              type="button"
              className={`mcr-push-tab${pushSource === 'github' ? ' active' : ''}`}
              onClick={() => setPushSource('github')}
            >
              From GitHub
            </button>
          </div>

          {pushSource === 'github' && (
            <div className="mcr-github-section">
              {ghConnected === null && <p className="mcr-gh-hint">Checking GitHub connection…</p>}
              {ghConnected === false && (
                <div className="mcr-gh-connect-wrap">
                  <p className="mcr-gh-hint">Connect your GitHub account to push directly from a repository.</p>
                  <button
                    type="button"
                    className="mcr-gh-connect-btn"
                    onClick={handleGhConnect}
                    disabled={ghConnecting}
                  >
                    {ghConnecting ? 'Opening GitHub…' : '⎆ Connect GitHub'}
                  </button>
                  {ghConnecting && (
                    <p className="mcr-gh-hint" style={{ marginTop: '0.5rem' }}>
                      Install the GitHub App in the new tab, then return here. Checking automatically…
                    </p>
                  )}
                </div>
              )}
              {ghConnected === true && (
                <form onSubmit={handleGhPush} className="mcr-build-form mcr-build-form-inline">
                  <div className="am-gh-connected-row">
                    <button
                      type="button"
                      className="gh-access-btn"
                      onClick={handleGhConnect}
                      disabled={ghConnecting}
                      title="Open GitHub to add or remove repositories from this installation"
                    >
                      <svg height="14" viewBox="0 0 16 16" width="14" fill="currentColor" aria-hidden="true"><path fillRule="evenodd" d="M8 0C3.58 0 0 3.58 0 8c0 3.54 2.29 6.53 5.47 7.59.4.07.55-.17.55-.38 0-.19-.01-.82-.01-1.49-2.01.37-2.53-.49-2.69-.94-.09-.23-.48-.94-.82-1.13-.28-.15-.68-.52-.01-.53.63-.01 1.08.58 1.23.82.72 1.21 1.87.87 2.33.66.07-.52.28-.87.51-1.07-1.78-.2-3.64-.89-3.64-3.95 0-.87.31-1.59.82-2.15-.08-.2-.36-1.02.08-2.12 0 0 .67-.21 2.2.82.64-.18 1.32-.27 2-.27.68 0 1.36.09 2 .27 1.53-1.04 2.2-.82 2.2-.82.44 1.1.16 1.92.08 2.12.51.56.82 1.27.82 2.15 0 3.07-1.87 3.75-3.65 3.95.29.25.54.73.54 1.48 0 1.07-.01 1.93-.01 2.2 0 .21.15.46.55.38A8.013 8.013 0 0016 8c0-4.42-3.58-8-8-8z"/></svg>
                      {ghConnecting ? 'Opening…' : 'Edit GitHub Access'}
                    </button>
                    <div className="mcr-gh-connected-badge">✓ GitHub connected</div>
                  </div>
                  <div className="input-group">
                    <div className="gh-label-row">
                      <label htmlFor="mcr-gh-repo">
                        Repository {ghReposLoading && <span className="mcr-label-hint">loading…</span>}
                      </label>
                      <button
                        type="button"
                        className="am-refresh-btn"
                        onClick={loadGhRepos}
                        disabled={ghPushLoading || ghReposLoading}
                        title="Refresh repository list"
                      >
                        {ghReposLoading ? 'Loading…' : '↻ Refresh'}
                      </button>
                    </div>
                    <select
                      id="mcr-gh-repo"
                      className="mcr-type-select"
                      value={ghSelectedRepo}
                      onChange={e => setGhSelectedRepo(e.target.value)}
                      disabled={ghPushLoading || ghReposLoading}
                    >
                      <option value="">— select repo —</option>
                      {ghRepos.map(r => (
                        <option key={r.full_name} value={r.full_name}>{r.full_name}</option>
                      ))}
                    </select>
                  </div>
                  <div className="input-group">
                    <label>Branch {ghBranchesLoading && <span className="mcr-label-hint">loading…</span>}</label>
                    <select
                      className="mcr-type-select"
                      value={ghSelectedBranch}
                      onChange={e => setGhSelectedBranch(e.target.value)}
                      disabled={ghPushLoading || !ghSelectedRepo || ghBranchesLoading}
                    >
                      <option value="">— select branch —</option>
                      {ghBranches.map(b => (
                        <option key={b} value={b}>{b}</option>
                      ))}
                    </select>
                  </div>
                  <div className="input-group">
                    <label htmlFor="mcr-gh-image-name">Image name</label>
                    <input
                      id="mcr-gh-image-name"
                      type="text"
                      value={ghImageName}
                      onChange={e => setGhImageName(e.target.value)}
                      placeholder="e.g. my-api"
                      disabled={ghPushLoading}
                    />
                  </div>
                  {ghPushMessage && <div className="mcr-message mcr-success">{ghPushMessage}</div>}
                  {ghPushError && <div className="mcr-message mcr-error">{ghPushError}</div>}
                  <div className="mcr-submit-wrap">
                    <button type="submit" className="mcr-submit-btn" disabled={ghPushLoading}>
                      {ghPushLoading ? 'Queueing build…' : 'Build & Push from GitHub'}
                    </button>
                  </div>
                </form>
              )}
            </div>
          )}

          {pushSource === 'zip' && (
          <form onSubmit={handleBuildPush} className="mcr-build-form mcr-build-form-inline">
            <p className="mcr-build-instruction">
              {'Enter an image name and upload a zip file with a '}
              <code>Dockerfile</code>
              {' at the root. We will upload your code and start building your image in MCR.'}
            </p>
            <div className="input-group">
              <label htmlFor="mcr-image-name-inline">Image name</label>
              <input
                id="mcr-image-name-inline"
                type="text"
                value={imageName}
                onChange={(e) => setImageName(e.target.value)}
                placeholder="e.g. my-app"
                disabled={pushLoading}
              />
            </div>
            <div className="input-group">
              <label>Image Type</label>
              <select
                className="mcr-type-select"
                value={imageType}
                onChange={(e) => setImageType(e.target.value)}
                disabled={pushLoading}
              >
                <option value="short">Job</option>
                <option value="long">Deployment</option>
              </select>
              <div className="mcr-type-desc">
                {imageType === 'short'
                  ? 'Job — lightweight image built for one-off jobs and batch tasks.'
                  : 'Deployment — persistent image for long-running APIs and deployed services. Add a healthcheck endpoint at /health so the load balancer can verify your service is running.'}
              </div>
            </div>
            <div className="input-group">
              <label>Code Upload</label>
              <div className="mcr-file-upload">
                <input
                  id="mcr-zip-input-inline"
                  type="file"
                  accept=".zip"
                  onChange={(e) => setZipFile(e.target.files?.[0] || null)}
                  disabled={pushLoading}
                  className="mcr-file-input-hidden"
                />
                <button
                  type="button"
                  className="mcr-select-file-btn"
                  onClick={() => document.getElementById('mcr-zip-input-inline')?.click()}
                  disabled={pushLoading}
                >
                  Select file
                </button>
                {zipFile && <span className="mcr-file-name">{zipFile.name}</span>}
              </div>
            </div>
            {pushLoading && uploadProgress !== null && uploadProgress < 100 && (
              <div className="mcr-progress-wrap">
                <div className="mcr-progress-bar">
                  <div className="mcr-progress-fill" style={{ width: `${uploadProgress}%` }} />
                </div>
                <span className="mcr-progress-label">Uploading… {uploadProgress}%</span>
              </div>
            )}
            {pushLoading && (uploadProgress === null || uploadProgress >= 100) && (
              <p className="mcr-upload-status">Building image — this may take a few minutes…</p>
            )}
            {pushMessage && <div className="mcr-message mcr-success">{pushMessage}</div>}
            {pushError && <div className="mcr-message mcr-error">{pushError}</div>}
            <div className="mcr-submit-wrap">
              <button type="submit" className="mcr-submit-btn" disabled={pushLoading}>
                {pushLoading
                  ? uploadProgress !== null && uploadProgress < 100
                    ? `Uploading ${uploadProgress}%…`
                    : 'Building…'
                  : 'Build & Push'}
              </button>
            </div>
          </form>
          )}
        </div>

        <div className="mcr-registry-panel">
          <div className="mcr-registry-header">
            <h3>Registry</h3>
            <div className="mcr-registry-header-actions">
              <div className="mcr-type-toggle">
                <button
                  type="button"
                  className={`mcr-type-btn${registryView === 'short' ? ' active' : ''}`}
                  onClick={() => setRegistryView('short')}
                  disabled={loading}
                >
                  Job
                </button>
                <button
                  type="button"
                  className={`mcr-type-btn${registryView === 'long' ? ' active' : ''}`}
                  onClick={() => setRegistryView('long')}
                  disabled={loading}
                >
                  Deployment
                </button>
              </div>
              <button
                type="button"
                className="mcr-refresh-btn"
                onClick={() => fetchImages(null)}
                disabled={loading}
              >
                Refresh
              </button>
            </div>
          </div>
          <div className="mcr-sort-row">
            <button className="mcr-sort-chip" onClick={() => handleSort(SORT_NAME)}>
              Image name {sortBy === SORT_NAME && (sortAsc ? '↑' : '↓')}
            </button>
            <button className="mcr-sort-chip" onClick={() => handleSort(SORT_SIZE)}>
              Size (MB) {sortBy === SORT_SIZE && (sortAsc ? '↑' : '↓')}
            </button>
            <button className="mcr-sort-chip" onClick={() => handleSort(SORT_DATE)}>
              Created {sortBy === SORT_DATE && (sortAsc ? '↑' : '↓')}
            </button>
          </div>
          <div className="mcr-cards-wrap">
            {sorted.length === 0 && !loading && (
              <div className="mcr-empty-card">No images in your registry.</div>
            )}
            {sorted.map((img) => (
              <div key={img.image_name} className="mcr-image-card">
                <div className="mcr-image-card-main">
                  <div className="mcr-image-name">{img.image_name}</div>
                  <div className="mcr-image-meta">
                    <span>{img.size_mb != null ? Number(img.size_mb).toFixed(2) : '–'} MB</span>
                    <span>{formatDate(img.created_on)}</span>
                  </div>
                </div>
                <button
                  type="button"
                  className="mcr-delete-btn"
                  onClick={() => handleDeleteClick(img.image_name)}
                  disabled={deleting === img.image_name}
                  title="Remove image"
                >
                  {deleting === img.image_name ? '…' : 'Delete'}
                </button>
              </div>
            ))}
          </div>
        </div>
      </div>
      {deleteConfirm && (
        <div className="cm-modal-overlay" onClick={() => setDeleteConfirm(null)}>
          <div className="cm-modal" onClick={e => e.stopPropagation()}>
            <h4 className="cm-modal-title">Delete Image</h4>
            <p className="cm-modal-body">
              Are you sure you want to delete <strong>{deleteConfirm}</strong>? This cannot be undone.
            </p>
            <div className="cm-modal-buttons">
              <button className="cm-btn cm-btn-ghost" onClick={() => setDeleteConfirm(null)}>Cancel</button>
              <button className="dm-btn-delete" onClick={handleDeleteConfirm}>Delete</button>
            </div>
          </div>
        </div>
      )}

      {showDeployWarning && (
        <div className="cm-modal-overlay" onClick={() => setShowDeployWarning(false)}>
          <div className="cm-modal" onClick={e => e.stopPropagation()}>
            <h4 className="cm-modal-title">Heads up</h4>
            <p className="cm-modal-body">
              You can build and push this deployment image to the registry, but it <strong>cannot be deployed</strong> on
              the shared cluster. To run it as a service, you'll need to upgrade to a dedicated cluster.
            </p>
            <div className="cm-modal-buttons">
              <button className="cm-btn cm-btn-ghost" onClick={() => setShowDeployWarning(false)}>Cancel</button>
              <button className="cm-btn cm-btn-primary" onClick={() => { setShowDeployWarning(false); doPush(); }}>
                Build &amp; Push anyway
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MCRManagement;
